import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import useCoachOverview from "./hooks/useCoachCreditOverview";
import useDashboardUser from "./hooks/useDashboardUser";
import useStudents from "./hooks/useStudents";
import { useCustomTokenAuth } from "../../hooks/useCustomTokenAuth";

const StudentCreditsContext = createContext(undefined);

export function StudentCreditsProvider({ children }) {
  const [tableOptions, setTableOptions] = useState({
    page: 1,
    sort: "daysLeft",
    order: "desc",
    subscriptionFilter: "",
    limit: 10,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [isPurchaseCreditsOpen, setIsPurchaseCreditsOpen] = useState(false);

  const { user, isUserLoading, fetchUser } = useDashboardUser();

  const { coachCreditOverview, fetchCoachCreditOverview } = useCoachOverview({
    coachId: user?.id,
  });

  const {
    handleAutoRenewal,
    handleUpgradeStudent,
    isStudentsLoading,
    students,
    totalStudents,
    fetchStudents,
  } = useStudents({
    user,
    tableOptions,
    search: "",
    fetchUser,
    fetchCoachCreditOverview,
  });

  const { signInWithCustomToken } = useCustomTokenAuth();

  useEffect(() => {
    (async () => await signInWithCustomToken())();
  }, [signInWithCustomToken]);

  const value = useMemo(
    () => ({
      // state
      tableOptions,
      isOpen,
      isPurchaseCreditsOpen,
      // setters
      setTableOptions,
      setIsOpen,
      setIsPurchaseCreditsOpen,
      // data/hooks
      user,
      isUserLoading,
      fetchUser,
      students,
      totalStudents,
      isStudentsLoading,
      coachCreditOverview,
      // actions
      handleAutoRenewal,
      handleUpgradeStudent,
      // (optionally expose) fetchStudents if needed elsewhere
      fetchStudents,
    }),
    [
      tableOptions,
      isOpen,
      isPurchaseCreditsOpen,
      user,
      isUserLoading,
      students,
      totalStudents,
      isStudentsLoading,
      coachCreditOverview,
      handleAutoRenewal,
      handleUpgradeStudent,
      fetchStudents,
    ]
  );

  return (
    <StudentCreditsContext.Provider value={value}>
      {children}
    </StudentCreditsContext.Provider>
  );
}

export function useStudentCredits() {
  const ctx = useContext(StudentCreditsContext);
  if (!ctx) {
    throw new Error(
      "useStudentCredits must be used within StudentCreditsProvider"
    );
  }
  return ctx;
}
